import React from 'react';
import { cva } from 'class-variance-authority';
import { motion } from 'motion/react';
import { duration } from './primitives/tokens';
import { Icon } from './Icon';
import styles from './Result.module.css';

const result = cva(styles.base);

type ResultStatus = 'success' | 'error' | 'info' | 'warning' | '403' | '404' | '500';

// Map each status to the icon color class (403/404/500 reuse token-based statuses)
const statusColorClass: Record<ResultStatus, string> = {
  success: styles.success,
  error: styles.error,
  info: styles.info,
  warning: styles.warning,
  '403': styles.warning,
  '404': styles.info,
  '500': styles.error,
};

const statusIcon: Record<ResultStatus, React.ReactNode> = {
  success: <Icon name="check_circle" size={64} />,
  error: <Icon name="cancel" size={64} />,
  info: <Icon name="info" size={64} />,
  warning: <Icon name="warning" size={64} />,
  '403': <Icon name="shield_lock" size={64} />,
  '404': <Icon name="description_off" size={64} />,
  '500': <Icon name="cloud_off" size={64} />,
};

interface ResultProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  status?: ResultStatus;
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  extra?: React.ReactNode;
  icon?: React.ReactNode;
}

/**
 * Result — status page for success, error, or empty states.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | status | 'success' \| 'error' \| 'info' \| 'warning' \| '403' \| '404' \| '500' | 'info' | Status type |
 * | title | ReactNode | — | Primary message |
 * | subTitle | ReactNode | — | Secondary message |
 * | extra | ReactNode | — | Action buttons |
 * | icon | ReactNode | — | Custom icon |
 *
 * @example
 * <Result status="success" title="Saved!" subTitle="Your thought is safe." />
 * <Result status="404" title="Page not found" extra={<Button>Go Home</Button>} />
 */
export function Result({ status = 'info', title, subTitle, extra, icon, className, style, ...props }: ResultProps) {
  return (
    <div
      className={result({ className })}
      style={style}
      {...props}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: duration.emphasis / 1000 }}
        className={statusColorClass[status]}
      >
        {icon ?? statusIcon[status]}
      </motion.div>
      {title && (
        <div className={styles.title}>
          {title}
        </div>
      )}
      {subTitle && (
        <div className={styles.subTitle}>
          {subTitle}
        </div>
      )}
      {extra && <div className={styles.extra}>{extra}</div>}
    </div>
  );
}
